import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Appearance, LoadingButton, LoadingButtonProps } from '@atlaskit/button';
import ExportIcon from '@atlaskit/icon/glyph/export';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { DarkLoadingButton } from '@css-xp/ppc-dark-button';
import { useGetAggregateCcpList, useGetEntitlementTotalCount } from '@css-xp/ppc-custom-hooks';
import {
  FetchEntitlementListForDownloadCsvQuery,
  FetchEntitlementListForDownloadCsvQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { fetchEntitlementListForDownloadCSV } from '../queries/fetch-entitlement-list-for-download-csv';
import { fetchInternalLicensesListForDownloadCSV } from '../queries/fetch-internal-licenses-list-for-download-csv';
import { SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';

export const headers = [
  SUBSCRIPTION_CONSTANTS.COLUMNS.ID,
  SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
  SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
  SUBSCRIPTION_CONSTANTS.COLUMNS.ENTITLEMENT_NUMBER,
  SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME,
  SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
  SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS,
  SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON,
  SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE,
  SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS,
  SUBSCRIPTION_CONSTANTS.COLUMNS.APPS,
  SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER_CONTACTS,
  SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY,
  SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS,
  SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED,
];

export const defaultSortOptions = {
  sortFields: ['lastPeriodEndDate', 'id'],
  sortFieldsDirections: [generatedTypesNew.SortDirection.DESC, generatedTypesNew.SortDirection.ASC],
};

export const useVariables = ({
  transactionAccountId,
  filterOptions = [],
  sortOptions,
  isSandboxSupportForSubscriptionsEnabled = false,
}: {
  transactionAccountId: string;
  filterOptions?: generatedTypesNew.FilterOption[];
  sortOptions?: generatedTypesNew.Maybe<generatedTypesNew.SortType>;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
}) => {
  return useMemo(() => {
    return {
      id: transactionAccountId,
      searchOptions: {
        sort: sortOptions,
      },
      filterOptions: [
        {
          key: 'not',
          value: JSON.stringify([
            ...(isSandboxSupportForSubscriptionsEnabled ? [] : [SUBSCRIPTION_CONSTANTS.sandboxFilter]),
          ]),
        },
        ...(filterOptions.filter(({ key }) => key !== 'not') ?? []),
      ],
      isRestricted: true,
    };
  }, [transactionAccountId, filterOptions, sortOptions, isSandboxSupportForSubscriptionsEnabled]);
};

interface CommonBaseProps {
  transactionAccountId: string;
  buttonAppearance?: Appearance;
  buttonText?: string;
  className?: string;
  isDarkTheme?: boolean;
  handleDownloadClick: (loadEntitlementList: () => Promise<any[] | undefined>) => void;
}

interface DownloadCSVButtonProps extends CommonBaseProps {
  buttonLoading: boolean;
  loadEntitlementList: () => Promise<any[] | undefined>;
}

export interface DownloadCSVCommonProps extends CommonBaseProps {
  variables: FetchEntitlementListForDownloadCsvQueryVariables;
  transactionAccountDataLoading?: boolean;
  loading?: boolean;
  shouldFetchInternalLicenses?: boolean;
  relatedDomains?: string[];
}

export const DownloadCSVButton = ({
  transactionAccountId,
  loadEntitlementList,
  buttonAppearance,
  buttonText,
  className,
  isDarkTheme,
  handleDownloadClick,
  buttonLoading,
}: DownloadCSVButtonProps) => {
  const buttonStyleId = isDarkTheme ? 'dark' : 'default';

  const props: LoadingButtonProps = {
    appearance: buttonAppearance,
    className: classNames(className, {
      'loading-button': buttonLoading,
    }),
    testId: buttonLoading ? `download-csv-loading-button-${buttonStyleId}` : `download-csv-button-${buttonStyleId}`,
    onClick: () => handleDownloadClick(loadEntitlementList),
    isLoading: buttonLoading,
    isDisabled: !transactionAccountId,
    type: 'button',
    iconBefore: <ExportIcon label="Export icon" size="small" />,
  };

  const buttonLabel = buttonText || 'Download CSV';

  return isDarkTheme ? (
    <DarkLoadingButton {...props}>{buttonLabel}</DarkLoadingButton>
  ) : (
    <LoadingButton {...props}>{buttonLabel}</LoadingButton>
  );
};

export const DownloadCSVUsingGetAggregateCcpList = ({
  transactionAccountId,
  variables,
  buttonAppearance,
  buttonText,
  className,
  isDarkTheme,
  transactionAccountDataLoading = false,
  handleDownloadClick,
  loading = false,
  shouldFetchInternalLicenses,
  relatedDomains,
}: DownloadCSVCommonProps) => {
  const graphqlClient = useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const totalCountVariables = shouldFetchInternalLicenses
    ? { ...variables, relatedDomains: relatedDomains }
    : variables;
  const { entitlementTotalCount } = useGetEntitlementTotalCount(totalCountVariables, shouldFetchInternalLicenses);

  const { isLoading: isEntitlementListLoading, getCcpData: loadEntitlementList } = useGetAggregateCcpList<
    FetchEntitlementListForDownloadCsvQuery,
    FetchEntitlementListForDownloadCsvQueryVariables
  >(
    graphqlClient,
    shouldFetchInternalLicenses ? fetchInternalLicensesListForDownloadCSV : fetchEntitlementListForDownloadCSV,
    {
      ...variables,
      searchOptions: {
        ...variables?.searchOptions,
        first: 100,
        offset: 0,
      },
      relatedDomains,
    },
    {
      totalCount: entitlementTotalCount,
      fetchType: 'parallel',
      paths: {
        dataPath: shouldFetchInternalLicenses
          ? 'data.transactionAccount.internalUseSubscriptions.nodes'
          : 'data.transactionAccount.productSubscriptionsInPPC.nodes',
      },
    }
  );

  const buttonLoading = loading || transactionAccountDataLoading || isEntitlementListLoading;

  return (
    <DownloadCSVButton
      {...{
        transactionAccountId,
        loadEntitlementList,
        buttonAppearance,
        buttonText,
        className,
        isDarkTheme,
        buttonLoading,
        handleDownloadClick,
      }}
    />
  );
};
